import React from 'react';
import { GameState, Investigator, UserAction, UserLanguage } from '../api';
import { findInvestigator } from '../utils/investigators';
import { ActionsList } from './ActionsList';
import { InvestigatorDigitalSkills } from './InvestigatorDigitalSheet';
import { MovementActionIcon } from './MovementActionIcon';

export const isUndoAction = (action: UserAction) => action.id === 'Undo' || action.id === 'Undo_One';
export const isControllerAction = (action: UserAction, playerName: string) =>
  !action.playerName || action.playerName.trim().toLowerCase() === playerName.trim().toLowerCase();

export function getUpkeepDecision(state: GameState, actions: UserAction[], playerName: string) {
  if (state.currentPhase !== 'Upkeep' || state.isFinalBattle) return undefined;
  const finish = actions.find(action => action.id === 'end_upkeep' && isControllerAction(action, playerName));
  const investigator = findInvestigator(state, finish?.investigatorName);
  if (!finish || !investigator) return undefined;
  return { finish, investigator,
    key: JSON.stringify([state.gameId, state.currentTurn, playerName.trim().toLowerCase(), investigator.name.toLowerCase(), 'upkeep-sliders']) };
}

type Props = {
  gameState: GameState; investigator: Investigator; actions: UserAction[]; finish: UserAction;
  loading: boolean; language?: UserLanguage; controlsInline: boolean; onExecuteAction: (id: string) => void;
};

export function UpkeepActionControls({ actions, finish, loading, onExecuteAction }: Pick<Props,
  'actions' | 'finish' | 'loading' | 'onExecuteAction'>) {
  return <div className="upkeep-action-controls" role="group" aria-label="Upkeep controls">
    {[finish, ...actions.filter(isUndoAction)].map(action => <button type="button" key={action.id}
      className={`upkeep-action-control ${action === finish ? 'upkeep-action-finish' : 'upkeep-action-undo'}`}
      disabled={loading || !!action.disabled} title={action.description || action.name}
      onClick={() => { if (!loading && !action.disabled) onExecuteAction(action.id); }}>
      <MovementActionIcon action={action} completion={action === finish} /><span>{action.name}</span>
    </button>)}
  </div>;
}

export function UpkeepActions(props: Props) {
  const { gameState, investigator, actions, finish, loading, language, controlsInline, onExecuteAction } = props;
  const pairs = investigator.name === 'Lily Chen' ? ['speed', 'fight', 'lore', 'max_sanity'] : ['speed', 'fight', 'lore'];
  const consumedSlider = (action: UserAction) => action.sliderPosition != null && Number.isInteger(action.sliderPosition) && action.sliderPosition >= 0 && action.sliderPosition < 4
    && action.investigatorName?.toLowerCase() === investigator.name.toLowerCase()
    && pairs.some(pair => action.id.startsWith(`set_${pair}_`));
  const sliders = actions.filter(consumedSlider).map(action => ({ ...action, disabled: loading || !!action.disabled }));
  const remaining = actions.filter(action => action !== finish && !isUndoAction(action) && !consumedSlider(action));
  return <div className="new-upkeep-actions">
    <article className="investigator-digital investigator-digital-upkeep" aria-label={`${investigator.name} skill adjustment`}>
      <header className="investigator-digital-header"><h3>{investigator.name}</h3><p>Skill Sliders Adjustment</p></header>
      <InvestigatorDigitalSkills investigator={investigator} gameState={gameState} availableActions={sliders}
        onExecuteAction={id => { if (!loading) onExecuteAction(id); }} />
    </article>
    {remaining.length > 0 && <ActionsList compact actions={remaining} language={language} loading={loading} onExecuteAction={onExecuteAction} />}
    {controlsInline && <UpkeepActionControls {...props} />}
  </div>;
}
